/* eslint-disable react/jsx-no-comment-textnodes */
/* eslint-disable import/first */
import type { ReactNode, ReactElement } from 'react';

import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, matchPath } from 'react-router-dom';
// material
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  List,
  Drawer,
  Typography,
  ListSubheader,
  Menu,
  MenuItem,
  Divider,
  TextField,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import {
  // @ts-ignore legacy boundary
  Add,
  // @ts-ignore legacy boundary
  KeyboardArrowDown,
  // @ts-ignore legacy boundary
  Search,
  // @ts-ignore legacy boundary
  BadgeOutlined,
} from '@mui/icons-material';
import { updateUserName } from '../../backend/user';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
// hooks
import useAuth from '../../hooks/useAuth';
import useTeams from 'src/hooks/useTeams';
import useGeofences from 'src/hooks/useGeofences';
import useGeozonesSelectorDialog from 'src/layouts/dashboard/useGeozonesSelectorDialog';
import useDebounce from 'src/hooks/useDebounce';
import useCreateDonationDisabled from 'src/hooks/useCreateDonationDisabled';
import isOwnerImpersonating from 'src/utils/adminImpersonation';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import useSidebarConfig from './SidebarConfig';
import SidebarItem from './SidebarItem';
import QuoStatusBox from './QuoStatusBox';
// utils
import { checkRoute } from 'src/utils/permissions';
import { sortBy } from 'lodash';
import { getPlanBySubscriptionProductId, hasPlanCapability } from 'careit';
import DialogCreateDonation from 'src/components/DialogCreateDonation';
const DialogCreateDonationComponent = DialogCreateDonation as any;
import CareitId from 'src/views/CareitId';
import { useBranding } from 'src/contexts/BrandContext';
import useBreakpoint from 'src/hooks/useBreakpoint';
import useTour from 'src/hooks/useTour';
import { careitIdTour } from 'src/config/tours/steps/careitId';
import { canSeeFindDonations } from 'src/utils/entryPoints';
import { PATH_DASHBOARD } from 'src/routes/paths';

export interface DashboardSidebarProps {
  isOpenSidebar?: boolean;
  onCloseSidebar?: (...args: any[]) => any;
  [key: string]: any;
}

interface NamePromptModalProps {
  open: boolean;
  onClose?: () => void;
  onSave: (payload: { first_name: string; last_name: string }) => void;
}

// Modal for collecting first_name and last_name if either is missing
function NamePromptModal({ open, onClose, onSave }: NamePromptModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation('translation');
  const [first_name, setFirstName] = useState(user?.first_name || '');
  const [last_name, setLastName] = useState(user?.last_name || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSave = async () => {
    if (!first_name.trim() || !last_name.trim()) {
      setError('Both first and last name are required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await updateUserName({
        first_name: first_name.trim(),
        last_name: last_name.trim(),
      });
      onSave({ first_name: first_name.trim(), last_name: last_name.trim() });
    } catch (e) {
      enqueueSnackbar(e.message || 'Failed to update name', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} disableEscapeKeyDown>
      <DialogTitle>{t('form.enter-your-name')}</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <TextField
            label={t('form.first-name')}
            value={first_name}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('form.last-name')}
            value={last_name}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
        </Box>
        {error && (
          <Typography color="error" variant="body2" mb={2}>
            {error}
          </Typography>
        )}
        <DialogActions>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {t('form.actions.save')}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
  // Protect sidebar from external CSS interference
  isolation: 'isolate',
  '& .MuiDrawer-paper': {
    isolation: 'isolate',
    '& .MuiList-root': {
      '& .MuiListItem-root': {
        '& .MuiListItemIcon-root': {
          color: 'inherit !important',
          '& svg, & .MuiSvgIcon-root': {
            color: 'inherit !important',
            fill: 'currentColor !important',
          },
        },
      },
    },
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  margin: theme.spacing(0, 2.5, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
}));

// ----------------------------------------------------------------------

function reduceChild({ user, array, item, pathname, level }) {
  const key = item.href + level;

  const items = (item.items ?? []).filter(({ href }) => checkRoute(user, href));
  if (items.length > 0) {
    const match = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    return [
      ...array,
      <SidebarItem
        key={key}
        user={user}
        level={level}
        badge={item.badge}
        icon={item.icon}
        info={item.info}
        href={item.href}
        title={item.title}
        open={Boolean(match)}
      >
        {renderSidebarItems({
          pathname,
          level: level + 1,
          items,
          user,
        })}
      </SidebarItem>,
    ];
  }

  // Do not render items that are allowed to collapse and have no children
  if (item.allowCollapse) {
    return array;
  }

  return [
    ...array,
    // @ts-ignore legacy boundary
    <SidebarItem
      key={key}
      user={user}
      level={level}
      href={item.href}
      badge={item.badge}
      icon={item.icon}
      info={item.info}
      title={item.title}
      crossOrigin={item.crossOrigin ?? false}
      alsoActiveOn={item.alsoActiveOn}
    />,
  ];
}

function renderSidebarItems({ user, items: i, pathname, level = 0 }) {
  let items = i;
  if (typeof items === 'function') items = items(user);

  return (
    <List disablePadding>
      {items.reduce(
        (array, item) => reduceChild({ user, array, item, pathname, level }),
        []
      )}
    </List>
  );
}

function groupTeams(teams) {
  let executive = [];
  let corporate = [];
  let admin = [];
  let staff = [];
  let driver = [];

  for (const team of teams) {
    if (team.is_executive) {
      executive.push(team);
    } else if (team.is_corporate) {
      corporate.push(team);
    } else if (team.is_admin) {
      admin.push(team);
    } else if (team.is_driver) {
      admin.push(team);
    } else {
      staff.push(team);
    }
  }

  return [executive, corporate, admin, staff, driver].map((group) =>
    sortBy(group, 'name')
  );
}

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
}: DashboardSidebarProps) {
  const { pathname } = useLocation();
  const MenuLinks = useSidebarConfig();
  const { user, invalidateUserData } = useAuth();
  const isImpersonating = isOwnerImpersonating(user);

  const [openNameModal, setOpenNameModal] = useState(false);
  const { breakpoint } = useBreakpoint();

  useEffect(() => {
    // Open if either first_name and last_name are missing — but not for a
    // partial (JWT-reconstructed) session, where names are absent because
    // /me is blocked, not because the user never set them.
    if (
      user?.id &&
      !user.is_partial_session &&
      (!user.first_name || !user.last_name)
    ) {
      setOpenNameModal(true);
    }
  }, [user]);

  const onNameSave = () => {
    invalidateUserData();
    setOpenNameModal(false);
  };
  const { teams, switchTo } = useTeams();
  const { teams: searchTeams, refresh: refetchTeams } = useTeams();
  const { t } = useTranslation('translation');
  // Brand dashboards call it "ID Card"; Careit keeps its own name.
  const isBrand = Boolean(useBranding().slug);
  const [careitIdOpen, setCareitIdOpen] = useState(false);
  const [donationDialogOpen, setDonationDialogOpen] = useState({
    isFoodRun: false,
    open: false,
  });
  const { handleChangeGeozones, GeozonesSelectorDialog } =
    useGeozonesSelectorDialog();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { activeGeofences, geofences } = useGeofences();
  const {
    startTour: startCareitIdTour,
    stopTour: stopCareitIdTour,
    completeTour: completeCareitIdTour,
    stepReached: careitIdStepReached,
    onClose: onCloseCareitIdTour,
    isRunning: isCareitIdTourRunning,
  } = useTour(careitIdTour);

  const [canSelectGeozones] = useMemo(() => {
    const isExecutive = !!user.is_executive;

    return [isExecutive];
  }, [user]);

  const teamsGrouped = useMemo(() => groupTeams(searchTeams), [searchTeams]);

  const [canCreateFoodRuns] = useMemo(() => {
    const currentPlan =
      getPlanBySubscriptionProductId(user.subscription_product_id) ?? 'free';
    const planActive = user.subscription_status?.toUpperCase() === 'ACTIVE';
    const plan = planActive ? currentPlan : 'free';

    const isNPO = !!user.is_non_profit;

    return [
      hasPlanCapability(plan, 'foodRuns') &&
        isNPO &&
        (user.is_admin || !user.is_driver),
    ];
  }, [user]);

  const canDisplayCreateDonationatioBtn = useMemo(
    () =>
      !(
        !user.team_id ||
        user.is_non_profit ||
        user.is_executive ||
        user.is_corporate
      ),
    [user]
  );

  useEffect(() => {
    if (isOpenSidebar && onCloseSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    refetchTeams();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenCareitId = () => {
    setCareitIdOpen(true);
    if (!isImpersonating) {
      startCareitIdTour(
        completeCareitIdTour,
        careitIdStepReached,
        onCloseCareitIdTour
      );
    }
  };

  const handleCloseCareitId = () => {
    setCareitIdOpen(false);
    if (isCareitIdTourRunning) {
      stopCareitIdTour();
    }
  };

  useEffect(() => {
    refetchTeams({
      search: debouncedSearchQuery,
    });
  }, [debouncedSearchQuery, refetchTeams]);

  const renderContent = (
    <>
      <Scrollbar>
        <Box sx={{ px: 2.5, pt: 3, pb: 1 }}>
          <RouterLink to="/">
            <Logo />
          </RouterLink>
        </Box>

        {user.account_type !== 'ADMIN' && (
          <AccountStyle onClick={handleClick}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: 'primary.main', fontWeight: 700 }}
              >
                {user.business_name}
              </Typography>
            </Box>
            <Box flex="1" />
            {teams.length > 1 && <KeyboardArrowDown color="disabled" />}
          </AccountStyle>
        )}

        {canCreateFoodRuns && (
          <CreateButton
            type="food-run"
            onClick={() =>
              setDonationDialogOpen({ isFoodRun: true, open: true })
            }
          />
        )}
        {canSeeFindDonations(user) && <FindDonationsButton />}
        {canDisplayCreateDonationatioBtn && (
          <CreateButton
            type="donation"
            onClick={() =>
              setDonationDialogOpen({ isFoodRun: false, open: true })
            }
          />
        )}

        <Menu
          sx={{
            '& .MuiPaper-root': {
              scrollbarWidth: 'thin',
            },
          }}
          anchorEl={anchorEl}
          open={open && teams.length > 1}
          onClose={handleClose}
        >
          <Box width="240px" />
          <MenuItem disabled>{t('sidebar.switch-teams')}:</MenuItem>
          {teams.length >= 10 ? (
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                mx: 1.2,
                mb: 1,
                backgroundColor: 'white',
                zIndex: 20,
                pt: 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                InputProps={{
                  endAdornment: (
                    <Search
                      sx={{
                        fill: (t) => t.palette.grey[500],
                      }}
                    />
                  ),
                }}
              />
            </Box>
          ) : null}
          {teamsGrouped
            .filter((group) => group.length > 0)
            // render each item in each group
            .map((group, _groupIndex) =>
              group
                .filter(({ team_id }) => team_id !== user.team_id)
                .map((team) => (
                  <MenuItem
                    key={team.team_id}
                    onClick={() => {
                      switchTo(team.team_id);
                      handleClose();
                    }}
                  >
                    <Typography
                      sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        pb: '24px',
                      }}
                    >
                      {team.name}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          bottom: 0,
                        }}
                      >
                        {!team.is_executive && !team.is_corporate ? (
                          <Typography variant="caption" paragraph={false}>
                            {team.province}, {team.city}, {team.street_address}
                          </Typography>
                        ) : (
                          <Typography variant="caption" paragraph={false}>
                            {team.is_executive
                              ? t('sidebar.executive-account')
                              : t('sidebar.corporate-account')}
                          </Typography>
                        )}
                      </Box>
                    </Typography>
                  </MenuItem>
                ))
            )
            // place a divider between groups
            .reduce(
              (otherGroups, group, index) =>
                otherGroups.length > 0
                  ? [
                      ...otherGroups,
                      <Divider key={`divider-${index}`} />,
                      ...group,
                    ]
                  : group,
              []
            )}
        </Menu>

        {geofences.length > 1 && canSelectGeozones && (
          <Box
            width="100%"
            px="22px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="outlined"
              onClick={handleChangeGeozones}
              sx={{
                backgroundColor: 'primary.main',
                color: '#ffffff',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  color: '#ffffff',
                  borderColor: 'primary.dark',
                },
              }}
            >
              {activeGeofences.length === 0
                ? `${t('executive.home.choose-geozones', {
                    activeGeofencesLength: activeGeofences.length,
                  })}`
                : `${t('executive.home.change-geozones', {
                    activeGeofencesLength: activeGeofences.length,
                  })}`}
            </Button>
          </Box>
        )}

        {MenuLinks.map((list, listIdx) => {
          let items = list.items;
          if (typeof items === 'function') items = items(user);

          // if no items pass the perms check then don't render the section
          if (!items.map(({ href }) => checkRoute(user, href)).find((x) => x))
            return <div key={`empty-${listIdx}`}></div>;

          return (
            items.length !== 0 && (
              <List
                disablePadding
                key={list.subheader || `section-${listIdx}`}
                subheader={
                  list.subheader ? (
                    <ListSubheader
                      disableSticky
                      disableGutters
                      sx={{
                        mt: 3,
                        mb: 2,
                        pl: 5,
                        color: 'text.primary',
                        typography: 'overline',
                      }}
                    >
                      {list.subheader.replace(/#/g, '')}
                    </ListSubheader>
                  ) : null
                }
              >
                {renderSidebarItems({
                  user,
                  items,
                  pathname,
                })}
              </List>
            )
          );
        })}
      </Scrollbar>

      {user.is_non_profit ? (
        <Box
          sx={{
            px: 2.5,
            pb: 3,
            pt: 3,
            borderTop: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          <Button
            fullWidth
            size="large"
            startIcon={<BadgeOutlined />}
            onClick={handleOpenCareitId}
            disableElevation
            variant="contained"
            data-tour={isImpersonating ? undefined : 'sidebar-careit-id'}
            sx={{
              py: 1.5,
              fontWeight: 700,
              borderRadius: 1,
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.12),
              color: 'primary.dark',
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.24),
                boxShadow: 'none',
              },
            }}
          >
            {isBrand ? t('id-card') : t('mobile.careit-id.title')}
          </Button>
          <Dialog
            fullScreen
            open={careitIdOpen}
            onClose={handleCloseCareitId}
            PaperProps={{ sx: { bgcolor: 'background.default' } }}
          >
            <Box sx={{ pb: 10 }}>
              <CareitId />
            </Box>
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                bgcolor: 'background.default',
                borderTop: '1px solid',
                borderColor: 'divider',
                zIndex: 1,
              }}
            >
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={handleCloseCareitId}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 1,
                  fontSize: '1rem',
                }}
              >
                {t('common.close', 'Close')}
              </Button>
            </Box>
          </Dialog>
        </Box>
      ) : null}

      {user.account_type === 'ADMIN' ? (
        <Box
          sx={{
            pt: 1.5,
            borderTop: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          <QuoStatusBox />
        </Box>
      ) : null}

      <Typography textAlign="center" variant="caption" sx={{ m: 1 }}>
        {t('careit-co')} &copy; {new Date().getFullYear()} -{' '}
        <RouterLink
          to={{
            pathname: 'https://careit.com/terms-of-service/',
          }}
          target="_blank"
          rel="noreferrer"
          underline="always"
          sx={{ color: 'text.primary' }}
        >
          {t('sidebar.terms-of-service')}
        </RouterLink>
      </Typography>
    </>
  );

  return (
    <RootStyle>
      {['xs', 'sm', 'md'].includes(breakpoint) ? (
        <Box
          display={{
            xl: 'none',
            lg: 'none',
            md: 'block',
            sm: 'block',
            xs: 'block',
          }}
        >
          <DialogCreateDonationComponent
            open={donationDialogOpen.open}
            onClose={() =>
              setDonationDialogOpen({ isFoodRun: false, open: false })
            }
            isFoodRun={donationDialogOpen.isFoodRun}
          />
          <Drawer
            open={isOpenSidebar}
            onClose={onCloseSidebar}
            PaperProps={{
              sx: { width: DRAWER_WIDTH },
            }}
          >
            {renderContent}
          </Drawer>
        </Box>
      ) : (
        <Box
          display={{
            xl: 'block',
            lg: 'block',
            md: 'none',
            sm: 'none',
            xs: 'none',
          }}
        >
          <Drawer
            open
            variant="persistent"
            PaperProps={{
              sx: { width: DRAWER_WIDTH, bgcolor: 'background.default' },
            }}
          >
            {renderContent}
          </Drawer>
        </Box>
      )}
      {donationDialogOpen.open && (
        <DialogCreateDonationComponent
          open={donationDialogOpen.open}
          onClose={() =>
            setDonationDialogOpen({ isFoodRun: false, open: false })
          }
          isFoodRun={donationDialogOpen.isFoodRun}
        />
      )}
      <GeozonesSelectorDialog />
      <NamePromptModal open={openNameModal} onSave={onNameSave} />
    </RootStyle>
  );
}

const FindDonationsButton = () => {
  const { t } = useTranslation('translation');

  return (
    <Box
      width="100%"
      px="22px"
      pb={1}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Button
        component={RouterLink}
        to={PATH_DASHBOARD.findItems.donations}
        sx={{
          width: '100%',
          color: 'text.primary',
          borderColor: 'grey.400',
          '&:hover': {
            borderColor: 'grey.600',
            bgcolor: 'action.hover',
          },
        }}
        variant="outlined"
        color="inherit"
        disableElevation
        startIcon={<Search />}
        data-tour="sidebar-find-donations"
      >
        {t('sidebar.find-donations')}
      </Button>
    </Box>
  );
};

const CreateButton = ({ onClick, type }) => {
  const { t } = useTranslation('translation');

  const [disabled, disabledReason] = useCreateDonationDisabled(type);

  const createBtnText = {
    donation: t('sidebar.create-donation'),
    'food-run': t('sidebar.create-food-run'),
  };

  return (
    <Tooltip title={disabled ? disabledReason : null}>
      <Box
        width="100%"
        px="22px"
        pb={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Button
          disabled={disabled}
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: 'grey.800',
            color: 'common.white',
            '&:hover': { bgcolor: 'grey.900' },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
          variant="contained"
          disableElevation
          onClick={onClick}
          startIcon={<Add />}
        >
          {createBtnText[type]}
        </Button>
      </Box>
    </Tooltip>
  );
};
